import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Settings, Save, Loader2, Plus, X } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminConfigPage() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newRegion, setNewRegion] = useState('');

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const response = await adminAPI.getConfig();
      setConfig(response.data);
    } catch (err) {
      toast.error('Failed to load configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateConfig(config);
      toast.success('Configuration saved');
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const addRegion = () => {
    if (!newRegion.trim()) return;
    if (config.regions.includes(newRegion.trim())) {
      toast.error('Region already exists');
      return;
    }
    setConfig({ ...config, regions: [...config.regions, newRegion.trim()] });
    setNewRegion('');
  };

  const removeRegion = (region) => {
    setConfig({ ...config, regions: config.regions.filter(r => r !== region) });
  };

  const addAttachment = (type, value) => {
    if (!value.trim()) return;
    const key = type === 'trailer' ? 'required_attachments_trailer' : 'required_attachments_storage';
    if (config[key].includes(value.trim())) {
      toast.error('Attachment already exists');
      return;
    }
    setConfig({ ...config, [key]: [...config[key], value.trim()] });
  };

  const removeAttachment = (type, value) => {
    const key = type === 'trailer' ? 'required_attachments_trailer' : 'required_attachments_storage';
    setConfig({ ...config, [key]: config[key].filter(a => a !== value) });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn max-w-3xl" data-testid="admin-config">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold">System Configuration</h1>
          <p className="text-muted-foreground mt-1">
            Manage inspection portal settings
          </p>
        </div>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Save className="mr-2 h-4 w-4" />
          )}
          Save Changes
        </Button>
      </div>

      {/* Cylinder Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Cylinder Configuration</CardTitle>
          <CardDescription>Set maximum cylinders allowed per inspection</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="max_cylinders">Maximum Cylinders</Label>
            <Input
              id="max_cylinders"
              type="number"
              min="1"
              max="50"
              value={config.max_cylinders}
              onChange={(e) => setConfig({ ...config, max_cylinders: parseInt(e.target.value) || 16 })}
              className="w-32"
            />
            <p className="text-xs text-muted-foreground">
              Maximum number of cylinders that can be recorded per inspection
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Validity Options */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Validity Periods</CardTitle>
          <CardDescription>Available validity period options (in months)</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {config.validity_options.map((option) => (
              <Badge key={option} variant="outline" className="text-sm">
                {option} months
              </Badge>
            ))}
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Contact administrator to modify validity options
          </p>
        </CardContent>
      </Card>

      {/* Regions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Inspection Regions</CardTitle>
          <CardDescription>Available regions for inspection selection</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {config.regions.map((region) => (
              <Badge key={region} variant="outline" className="pl-3 pr-1 py-1">
                {region}
                <button
                  onClick={() => removeRegion(region)}
                  className="ml-2 hover:bg-muted rounded p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex gap-2">
            <Input
              value={newRegion}
              onChange={(e) => setNewRegion(e.target.value)}
              placeholder="Add new region..."
              className="max-w-xs"
              onKeyDown={(e) => e.key === 'Enter' && addRegion()}
            />
            <Button variant="outline" size="sm" onClick={addRegion}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Required Attachments - Trailer */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Required Attachments - Trailer</CardTitle>
          <CardDescription>Documents required for trailer inspections</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {config.required_attachments_trailer.map((att) => (
              <Badge key={att} variant="outline" className="pl-3 pr-1 py-1">
                {att.replace('_', ' ')}
                <button
                  onClick={() => removeAttachment('trailer', att)}
                  className="ml-2 hover:bg-muted rounded p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
          <AttachmentInput onAdd={(v) => addAttachment('trailer', v)} />
        </CardContent>
      </Card>

      {/* Required Attachments - Storage */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Required Attachments - CNG Storage</CardTitle>
          <CardDescription>Documents required for CNG storage facility inspections</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            {config.required_attachments_storage.map((att) => (
              <Badge key={att} variant="outline" className="pl-3 pr-1 py-1">
                {att.replace('_', ' ')}
                <button
                  onClick={() => removeAttachment('storage', att)}
                  className="ml-2 hover:bg-muted rounded p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
          <AttachmentInput onAdd={(v) => addAttachment('storage', v)} />
        </CardContent>
      </Card>
    </div>
  );
}

function AttachmentInput({ onAdd }) {
  const [value, setValue] = useState('');
  
  const handleAdd = () => {
    if (value.trim()) {
      onAdd(value.trim().toLowerCase().replace(/\s+/g, '_'));
      setValue('');
    }
  };

  return (
    <div className="flex gap-2">
      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Add attachment type..."
        className="max-w-xs"
        onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
      />
      <Button variant="outline" size="sm" onClick={handleAdd}>
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
}
